import { uniq } from 'lodash';
import { RegionType } from '@wix/shoutout-email-model-v3';
import { SectionType } from '../../types';
import { textCssClasses } from '../../sections/section-text';
import { getEmailBackgroundColor } from '../get-email-background-color';
import { mobileFullWidthColumnClassName, mobileNoWrapColumnClassName, } from '../defaults';
import * as utils from './css-utils-top-padding';
import { getAllTextRegions } from './html';
import { getActualSectionPaddingForSmallScreenCss } from './get-section-padding-tweaks-for-small-screen-css';
import { getMobileLineHeightClasses } from './get-mobile-line-height-classes';
import { getMobileFontSizeClasses } from './get-mobile-font-size-classes';
import { getFooterSectionStyle } from './get-footer-section-style';
import { getMixCss, getMixSmallScreenCss, getMobileMixMulticolumnCss, } from './get-mix-section-style';
import { getItemGridSmallScreenCss } from './get-item-grid-section-style';
const { smScreen: footerSmScreen, lgScreen: footerLgScreen, all: footerAllScreen, } = getFooterSectionStyle();
const getCommonStyleGenerators = () => {
    return {
        smScreen: [
            getActualSectionPaddingForSmallScreenCss,
            getSeparatorTweaksForSmallScreensCss,
            getMobileTypographyClasses,
            getTextSmallScreenCss,
            getColumnSmallScreenCss,
        ],
        lgScreen: [getTopMessageSeparatorLargeScreenSize],
        all: [
            getLinksUnderlineStyle,
            getLinksDefaultColorStyle,
            getButtonDefaultCss,
            getOutlookBgColor,
        ],
    };
};
export const buildGlobalCssWithSectionStyleGenerators = (cssGeneratorParams) => {
    const generateCss = (generators) => generators.map((f) => f(cssGeneratorParams)).join(' ');
    const generateMixSmScreenCss = (sections) => uniq(sections
        .filter((section) => section.type === RegionType.Mix)
        .map((section) => getMobileMixMulticolumnCss(section))
        .join(' ')
        .split('}')).join('}');
    const { breakPoint, sections, buildForMobile } = cssGeneratorParams;
    const commonStyleGenerators = getCommonStyleGenerators();
    const sectionTypesUsed = uniq(sections.map((section) => section.type));
    sectionTypesUsed.forEach((sectionType) => {
        switch (sectionType) {
            case SectionType.footer:
                commonStyleGenerators.smScreen.push(...footerSmScreen);
                commonStyleGenerators.lgScreen.push(footerLgScreen);
                commonStyleGenerators.all.push(footerAllScreen);
                return;
            case SectionType.follow:
                commonStyleGenerators.smScreen.push(getSocialFollowSmScreenCss);
                return;
            case SectionType.mix:
                commonStyleGenerators.smScreen.push(getMixSmallScreenCss);
                commonStyleGenerators.all.push(getMixCss);
                return;
            case SectionType.subscribe:
                commonStyleGenerators.smScreen.push(getSubscribeSmallScreenCss);
                commonStyleGenerators.all.push(getSubscribersSectionStyle);
                return;
            case SectionType.itemGrid:
                commonStyleGenerators.smScreen.push(getItemGridSmallScreenCss);
                return;
            default:
                return;
        }
    });
    const marginPadding = utils.fixTopSpacingOnMobile(sections);
    let smScreenCss = '';
    if (buildForMobile) {
        const regionSpecificMobileCss = generateMixSmScreenCss(sections);
        smScreenCss = `
    @media only screen and (max-width: ${breakPoint}) {
      ${generateCss(commonStyleGenerators.smScreen)}
      ${regionSpecificMobileCss}
      .html-email-body-section-topSpacer > div {
        height: 0px!important;
      }
      .first-region-section > table > tbody > tr > td {
        padding-top: ${marginPadding.margin}px!important;
      }
      .first-region-section .html-email-body-column > table > tbody > tr > td {
        padding-top: ${marginPadding.padding}px!important;
      }
    }

  `;
    }
    return `${smScreenCss}

    @media only screen and (min-width: ${breakPoint}) {
      ${generateCss(commonStyleGenerators.lgScreen)}
    }

    ${generateCss(commonStyleGenerators.all)}
  `;
};
function getColumnSmallScreenCss() {
    return `.${mobileFullWidthColumnClassName} { max-width: 100%!important; }
    .${mobileNoWrapColumnClassName} { max-width: unset!important; }
  `;
}
function getSeparatorTweaksForSmallScreensCss() {
    return `
    .social-icon { width: 36px!important; height: 36px!important; }
    .social-icon-spacer { width: 12px!important; }
    .social-icon-wrap { margin: 12px!important; }
  `;
}
function getTextSmallScreenCss() {
    return `
    .${textCssClasses.text} {
      padding: 0!important;
    }
  `;
}
function getSubscribeSmallScreenCss() {
    return `
  .html-emails-subscribe-button {
    padding-top: 15px!important;
  }

  .html-email-body-column.html-emails-subscribe-input-container > table > tbody > tr > td {
    padding-bottom: 0px!important;
  }
  `;
}
function getLinksUnderlineStyle() {
    return `
    a, a:visited { text-decoration: none; }
  `;
}
function getLinksDefaultColorStyle() {
    return `
    a, a:active, a:visited, a:hover, a:link {
      color: #109FFF;
    }
  `;
}
function getSocialFollowSmScreenCss() {
    return `
    .social-follow-element > td {
      padding: 12px!important;
    }
    .social-follow-element img {
      width:36px!important;
      height:36px!important
    }
  `;
}
export function getButtonDefaultCss() {
    return `
    .html-emails-button > table > tbody > tr > td {
      overflow: hidden;
    }
    .html-emails-button > div > table > tbody > tr > td {
      overflow: hidden;
    }
  `;
}
function getTopMessageSeparatorLargeScreenSize() {
    return `.html-emails-background > tbody > tr > td {padding: 24px 0!important;}`;
}
export function getOutlookBgColor({ composerData }) {
    const color = getEmailBackgroundColor(composerData);
    return `
    [class="x_html-emails-background"] {
      background-color: ${color}!important;
    }`;
}
function getMobileTypographyClasses(params) {
    const { experiments, composerData, sections } = params;
    const htmls = getAllTextRegions(composerData, sections, experiments);
    return [
        getMobileFontSizeClasses(htmls, params),
        getMobileLineHeightClasses(htmls, params),
    ].join(' ');
}
function getSubscribersSectionStyle() {
    return `
    .html-emails-subscribe-button-container {
      overflow: hidden;
    }

    .html-emails-subscribe-button p {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  `;
}
//# sourceMappingURL=build-global-css.js.map